import React from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { FeatureToggleProvider } from '@contexts/FeatureToggleContext';
import { ensureRegionParam } from '@helpers/ensureRegionParam';
import { THANK_YOU_PATHS } from '@helpers/constants';

export const wrapRootElement = ({ element }) => (
  <FeatureToggleProvider>
    <GoogleReCaptchaProvider reCaptchaKey={process.env.GATSBY_GOOGLE_RECAPTCHA_KEY}>
      <style>{`.grecaptcha-badge { visibility: hidden; }`}</style>
      {element}
    </GoogleReCaptchaProvider>
  </FeatureToggleProvider>
);

export const onRouteUpdate = ({ location }) => {
  scrollToAnchor(location);
  sessionStorage.setItem('locationKey', location.key);
  ensureRegionParam(location);

  window.dataLayer = window.dataLayer || [];

  const THANK_YOU_ROUTES = Object.values(THANK_YOU_PATHS).flat();
  const normalizePath = (path) => path.replace(/\/$/, '');
  const currentPath = normalizePath(location.pathname);

  // Check if current page is a Thank You page
  const isThankYouPage = THANK_YOU_ROUTES.some((path) => currentPath.startsWith(normalizePath(path)));

  if (isThankYouPage) {
    const daxId = location.state?.orderId;

    if (daxId) {
      window.dataLayer.push({
        event: 'formSubmissionComplete',
        orderId: daxId,
        pagePath: location.pathname,
      });
    }
  }
};

function scrollToAnchor(location) {
  window.requestAnimationFrame(() => {
    if (location && location.hash) {
      // Get anchor element we are trying to show.
      let scrollTarget = location.hash.replace('#', '');

      // Because of how the header component id property is used we're having to use the text of the header we want to scroll to!
      // So decode the header id e.g. 'Course%20fee' becomes 'Course fee'
      scrollTarget = decodeURIComponent(scrollTarget);

      const element = document.getElementById(scrollTarget);

      if (element === null) return;

      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
}

export const shouldUpdateScroll = ({ routerProps: { location }, getSavedScrollPosition }) => {
  window.history.scrollRestoration = 'manual';
  const [savedScrollPositionLeft, savedScrollPositionTop] = getSavedScrollPosition(location) || [0, 0];

  if (location && location.hash) {
    scrollToAnchor(location);
    return;
  }

  setTimeout(() => {
    window.scrollTo({
      top: savedScrollPositionTop,
      left: savedScrollPositionLeft,
      behavior: 'instant',
    });
  }, 600);

  return false;
};
